import type { UIViewInjectedProps } from '@uirouter/react';
import { useState } from 'react';

import { getTranslatedString } from '@/utils';
import { api } from '@/api';
import { Drawer } from '@/components/drawer';
import { Upload } from '@/components/upload';
import { message, Select, Form } from 'antd';
import type { RcFile } from 'antd/lib/upload';
import { FarmerSourceDocumentType } from '@/types/enums';
import { farmerStore } from '@/stores';
import { CooperativeSearchSelect } from '@/components/form-fields';

export const CreateFarmersFromExcelByType = ({ transition }: UIViewInjectedProps) => {
  const [form] = Form.useForm();
  const [sourceDocumentType, setSourceDocumentType] = useState<FarmerSourceDocumentType | null>(null);

  const sourceDocumentTypeOptions = [
    { value: FarmerSourceDocumentType.RA, label: 'Rainforest Alliance' },
    { value: FarmerSourceDocumentType.FT, label: 'Fairtrade' },
    { value: FarmerSourceDocumentType.MERIDIA_DOCUMENT, label: 'Meridia' },
  ];

  const request = async (fileList: RcFile[]) => {
    if (!sourceDocumentType) {
      message.error('Please select a document type first.');
      throw new Error('Document type not selected');
    }
    // only one file validation
    if (fileList.length !== 1) {
      message.error('Supports only one file');
      throw new Error('Invalid number of files');
    }

    // Excel file validation
    const validExtensions = ['.xls', '.xlsx'];
    const invalidFiles = fileList.filter(
      (file: RcFile) => !validExtensions.some((ext) => file.name.toLowerCase().endsWith(ext)),
    );
    if (invalidFiles.length > 0) {
      message.error('You can upload only Excel files');
      throw new Error('Invalid file extension');
    }

    const file: RcFile = fileList[0];
    const cooperativeId = form.getFieldValue('cooperativeId') as number | undefined;

    await api.farmers.createFromExcelByType({ file, sourceDocumentType, cooperativeId }).source;
    await farmerStore.refresh();
    transition.router.stateService.go('base-layout.farmers');
  };

  const onClose = () => {
    transition.router.stateService.go('base-layout.farmers');
  };

  return (
    <Drawer onClose={onClose} visible={true} title={getTranslatedString('farmers.create-from-excel-by-type')}>
      <Form form={form} layout="vertical">
        <Form.Item label={getTranslatedString('farmers.sourceDocumentType')} required>
          <Select
            placeholder="Select document type"
            onChange={(value) => {
              setSourceDocumentType(value as FarmerSourceDocumentType);
              form.setFieldsValue({ cooperativeId: undefined });
            }}
            options={sourceDocumentTypeOptions}
          />
        </Form.Item>
        <CooperativeSearchSelect
          name="cooperativeId"
          i18nLabel="farmers.cooperative"
          allowClear={true}
          readOnly={!sourceDocumentType}
        />
        <Form.Item>
          <Upload
            request={request}
            disabled={!sourceDocumentType}
            multiple={false}
            accept=".xls,.xlsx"
            title="Click or drag a farmer list Excel file here"
            description="Single .xls or .xlsx file."
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
};
